import React, { useEffect, useState } from "react"
import Modal from "aldoo-ra/Modal"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import { showMessageDialog } from "aldoo-ra/MessageDialog"

/**
 * PermissionCheck Component
 * 
 * A self-contained component that monitors for permission errors and shows appropriate dialogs.
 * Use as a single tag in components that need permission checking.
 * 
 * @param {Object} props
 * @param {string} props.permission - The required permission (e.g., "role:newsletter")
 * @param {string} [props.redirect="/admin"] - URL to redirect to when access is denied
 * @param {string} [props.title="Access Denied"] - Title for the permission dialog
 * @param {string} [props.message] - Custom message for the permission dialog
 * @param {string} [props.redirectLabel="Return to Admin"] - Label for the dialog button
 * @param {Function} [props.onPermissionDenied] - Custom handler when permission is denied
 * 
 * Usage:
 * function MyComponent() {
 *   return (
 *     <div>
 *       <PermissionCheck permission="role:newsletter" />
 *       <PermissionCheck permission="role:billing" redirect="/dashboard" redirectLabel="Go to Dashboard" />
 *       {/* Rest of component *\/}
 *     </div>
 *   )
 * }
 */
const PermissionCheck = ({
  permission,
  redirect = '/admin',
  title = 'Access Denied',
  message,
  redirectLabel = 'Return to Admin',
  onPermissionDenied
}) => {
  const [showDialog, setShowDialog] = useState(false)
  
  // Default message based on permission
  const defaultMessage = message || 
    `You do not have permission to access this feature. Please contact your administrator to request the "${permission}" permission.`

  /**
   * Check if a response contains permission errors
   * @param {Object} response - API response to check
   * @returns {boolean} - True if permission error found and handled
   */
  const handlePermissionError = (response) => {
    if (response && 
        response.error === 'permissions_required' && 
        response.required?.includes(permission)) {
      
      if (onPermissionDenied) {
        onPermissionDenied(response)
      } else {
        setShowDialog(true)
      }
      
      return true
    }
    return false
  }

  const handleConfirm = () => {
    setShowDialog(false)
    window.location.href = redirect
  }

  // Monitor console errors for realtime permission issues
  useEffect(() => {
    const originalConsoleError = console.error
    
    console.error = (...args) => {
      const errorMessage = args.join(' ')
      
      // Check for realtime permission errors
      if (errorMessage.includes('Permission denied') && 
          errorMessage.includes(permission)) {
        handlePermissionError({ 
          error: 'permissions_required', 
          required: [permission] 
        })
      }
      
      // Call original console.error
      originalConsoleError.apply(console, args)
    }

    return () => {
      console.error = originalConsoleError
    }
  }, [permission])

  // Store the handler globally so it can be accessed by other components
  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (!window._permissionCheckers) {
        window._permissionCheckers = {}
      }
      window._permissionCheckers[permission] = handlePermissionError
    }

    return () => {
      if (typeof window !== 'undefined' && window._permissionCheckers) {
        delete window._permissionCheckers[permission]
      }
    }
  }, [permission, handlePermissionError])

  // Render the permission dialog when needed
  return (
    <Modal
      isOpen={showDialog}
      onClose={() => {}} // Disable close - no X button
      variant="admin_centered"
      allowCloseBtn={false}
      closeOnClickOutside={false}
      closeOnEscape={false}
      title={title}
      styleOverrides={{
        title: "flex-1 text-xl font-bold text-admin_text text-center"
      }}
    >
      <div className="text-center py-4">
        <Typography variant="admin_body" className="mb-6 text-admin_text_secondary leading-relaxed">
          {defaultMessage}
        </Typography>
        
        <div className="flex justify-center">
          <Button 
            variant="admin_primary" 
            onClick={handleConfirm}
            className="px-8 py-3 text-base font-medium"
          >
            {redirectLabel}
          </Button>
        </div>
      </div>
    </Modal>
  )
}

/**
 * Hook to access permission checking function for a specific permission
 * Components can use this to manually check permissions on API responses
 * 
 * Usage in components:
 * const handlePermissionError = usePermissionCheck('role:newsletter')
 * 
 * // In API call:
 * const response = await NewsletterAPI.getReleases()
 * if (handlePermissionError && handlePermissionError(response)) {
 *   return // Stop processing if permission error
 * }
 */
export const usePermissionCheck = (permission) => {
  if (typeof window === 'undefined') return null
  return window._permissionCheckers?.[permission] || null
}

/**
 * Utility function to create a permission error handler
 * Can be used outside of React components
 * 
 * @param {string} permission - Required permission
 * @param {Object} options - Configuration options
 * @returns {Function} Permission error handler function
 */
export const createPermissionHandler = (permission, options = {}) => {
  const {
    redirect = '/admin',
    title = 'Access Denied',
    message = `You do not have permission to access this feature. Please contact your administrator to request the "${permission}" permission.`,
    redirectLabel = 'Return to Admin',
    onPermissionDenied
  } = options

  return (response) => {
    if (response && 
        response.error === 'permissions_required' && 
        response.required?.includes(permission)) {
      
      if (onPermissionDenied) {
        onPermissionDenied(response)
      } else {
        // Fallback to showMessageDialog for utility function usage
        showMessageDialog({
          title,
          message,
          btnLabel: redirectLabel,
          showCloseX: false,
          onConfirm: () => {
            window.location.href = redirect
          }
        })
      }
      
      return true
    }
    return false
  }
}

export default PermissionCheck